import { type FC, lazy, useEffect, useRef } from "react"
import styled from "styled-components"

import { type ChapterDripInterval } from "@forento/core/models/course"

import Button from "~/components/Button"
import Dropdown from "~/components/Dropdown"
import FeatureIntroduction from "~/components/FeatureIntroduction"
import InputField from "~/components/InputField"
import RadioButtons from "~/components/RadioButtons"
import { useIsUnsavedChangesOpen } from "~/contexts/ToastContext"
import { usePlatform } from "~/contexts/UserContext"
import routes from "~/utilities/routes"
import { lightTextColor } from "~/utilities/styles"

const ChapterDrippingImage = lazy(() => import("./ChapterDrippingImage"))

const chapterDripOptions: { id: ChapterDripInterval | "none"; title: string }[] = [
	{ id: "none", title: "Release all instantly" },
	{ id: "day", title: "One chapter per day" },
	{ id: "week", title: "One chapter per week" },
	{ id: "month", title: "One chapter per month" },
]

const intervalLabels: Record<ChapterDripInterval, string> = { day: "day", week: "week", month: "month" }

// Formats a date for a datetime-local input in the creator's time zone.
export function toLocalDateTimeString(date: Date): string {
	const pad = (value: number) => value.toString().padStart(2, "0")
	return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

export function parseLocalDateTime(value: string): Date | null {
	const date = new Date(value)
	return Number.isNaN(date.valueOf()) ? null : date
}

function getDefaultStartDate(): string {
	const date = new Date()
	date.setDate(date.getDate() + 1)
	date.setHours(0, 0, 0, 0)
	return toLocalDateTimeString(date)
}

type Props = {
	interval: ChapterDripInterval | null
	onChange(interval: ChapterDripInterval | null): void
	// A datetime-local value. When set, every student gets the chapters on the same calendar dates.
	startDate: string | null
	onStartDateChange(startDate: string | null): void
}
const ChapterDripping: FC<Props> = ({ interval, onChange, startDate, onStartDateChange }) => {
	const platform = usePlatform()!
	const startDateRef = useRef<HTMLDivElement>(null)
	const isCalendarSchedule = startDate !== null
	const isUnsavedChangesOpen = useIsUnsavedChangesOpen()
	const hasRevealedStartDate = useRef(isCalendarSchedule)

	// Reveal the date field when the creator switches to calendar dates. Switching opens the unsaved changes bar, so wait
	// until it is open: the provider reserves room for it in the same commit, before the next frame scrolls.
	useEffect(() => {
		if (!isCalendarSchedule) {
			hasRevealedStartDate.current = false
			return
		}
		if (hasRevealedStartDate.current || !isUnsavedChangesOpen) return
		const frame = requestAnimationFrame(() => {
			hasRevealedStartDate.current = true
			startDateRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" })
		})
		return () => cancelAnimationFrame(frame)
	}, [isCalendarSchedule, isUnsavedChangesOpen])

	return (
		<Container>
			<FeatureIntroduction
				illustration={ChapterDrippingImage}
				title="Chapter dripping"
				text="Enable this option to activate the dripping feature for course chapters. This allows you to release course content gradually over time, ensuring that users receive new chapters at a controlled pace. This method helps maintain engagement and allows users to absorb information more effectively."
			>
				{platform.plan.access.chapterDripping ? (
					<>
						<StyledDropdown
							items={chapterDripOptions}
							selectedItemId={interval ?? "none"}
							onChange={value => {
								const parsedValue = chapterDripOptions.find(option => option.id === value)?.id ?? "none"
								onChange(parsedValue !== "none" ? parsedValue : null)
								if (parsedValue === "none") onStartDateChange(null)
							}}
						/>
						{interval !== null && (
							<div className="flex w-full max-w-[500px] flex-col gap-4 text-left">
								<RadioButtons
									options={[
										{
											value: "chapter-drip-student",
											label: "From each student's start",
											description: `The first chapter opens when a student starts the course, then one more chapter each ${intervalLabels[interval]}.`,
										},
										{
											value: "chapter-drip-calendar",
											label: "On fixed calendar dates",
											description: `Every student gets the same release dates, such as for an advent calendar or a cohort. Students who join late get every chapter released so far right away.`,
										},
									]}
									value={startDate !== null ? "chapter-drip-calendar" : "chapter-drip-student"}
									onChange={value =>
										onStartDateChange(
											value === "chapter-drip-calendar" ? getDefaultStartDate() : null,
										)
									}
								/>
								{startDate !== null && (
									<div ref={startDateRef}>
										<InputField
											inputType="datetime-local"
											label="First chapter release date"
											value={startDate}
											onChange={onStartDateChange}
											required
										/>
									</div>
								)}
							</div>
						)}
						<Button variant="link" onClick={routes.settings.studentEmail.index()}>
							Customize chapter dripping email
						</Button>
					</>
				) : (
					<>
						<Text>You need to upgrade your plan to access this feature.</Text>
						<Button variant="primary" onClick={routes.settings.billing.plans()}>
							Browse plans
						</Button>
					</>
				)}
			</FeatureIntroduction>
		</Container>
	)
}

const Container = styled.div`
	margin-top: 24px;
`

const StyledDropdown = styled(Dropdown)`
	width: 100%;
	max-width: 250px;
`

const Text = styled.p`
	color: ${lightTextColor};
	text-align: center;
	max-width: 500px;
	font-style: italic;
`

export default ChapterDripping
